import { useMemo } from 'react';
import styled from 'styled-components';

import { useInfiniteQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import DataTable from 'components/shared/DataTable';
import DocumentLink from 'components/shared/DocumentLink';
import Tooltip from 'components/shared/Tooltip';
import useIsMobile from 'hooks/useIsMobile';
import { dateAtTime } from 'lib/date';
import Attachment from 'models/Attachment';
import { attachmentsQueryKeys, indexAttachments } from 'services/api/attachments';
import colors from 'styles/theme/colors';
import { BodySmallBold } from 'styles/typography';
import ErrorWarningIcon from 'svg/ErrorWarningIcon';

const columnHelper = createColumnHelper<Attachment>();

function getColumns() {
  return [
    columnHelper.accessor((row) => row.filename, {
      header: 'File Name',
      id: 'filename',
      cell: (info) => {
        const attachment = info.row.original;
        const filename = info.getValue() || `Untitled ${attachment.contentType} ${info.row.index}`;
        return (
          <FileNameContainer>
            {attachment.extractDataAIJob?.isErrored && (
              <Tooltip
                text='File failed to process and will not be included in the admission summary'
                position='bottom'>
                <IconContainer>
                  <ErrorWarningIcon />
                </IconContainer>
              </Tooltip>
            )}
            <DocumentLink entry={attachment} attachableModel='Review::Admission'>
              <StyledFilename className='first-column' color={colors.blue100}>
                {filename}
              </StyledFilename>
            </DocumentLink>
          </FileNameContainer>
        );
      },
    }),
    columnHelper.accessor(
      (row) => ({ userFullName: row.createdByUser?.fullName, groupName: row.createdByGroup?.name }),
      {
        id: 'createdByUser',
        header: 'Uploaded By',
        cell: (info) => {
          const { userFullName, groupName } = info.getValue();
          if (!userFullName) return <div>&mdash;</div>;
          return (
            <div>
              {userFullName} {groupName && `at ${groupName}`}
            </div>
          );
        },
      }
    ),
    columnHelper.accessor((row) => row.createdAt, {
      header: 'Uploaded On',
      id: 'createdAt',
      cell: (info) => (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>{dateAtTime(info.getValue())}</div>
          </div>
        </div>
      ),
    }),
  ];
}

type Props = {
  reviewId: string;
};

export default function AdmissionFilesTab({ reviewId }: Props) {
  const isMobile = useIsMobile();

  const queryParams = useMemo(
    () => ({
      reviewId,
      attachableModel: 'Review::Admission',
      pageSize: 10,
      include: 'blob,extractDataAIJob,createdByUser,createdByGroup',
      archived: false,
      sortBy: 'createdAt desc',
    }),
    [reviewId]
  );

  const query = useInfiniteQuery({
    queryKey: attachmentsQueryKeys.index(queryParams),
    queryFn: ({ pageParam, signal }) => indexAttachments({ ...queryParams, page: pageParam as number }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      return lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined;
    },
    enabled: !!reviewId,
  });

  const files = query.data?.pages.flatMap((page) => page.data) ?? [];
  const columns = useMemo(() => getColumns(), []);

  return (
    <DataTable<Attachment>
      data={files}
      columns={columns}
      loading={query.isLoading || query.isFetchingNextPage}
      hasMore={query.hasNextPage}
      onBodyEndReached={() => {
        if (query.hasNextPage) {
          query.fetchNextPage();
        }
      }}
      hasFooter={false}
      maxHeight='none'
      defaultSortBy={[{ id: 'createdAt', desc: true }]}
      columnVisibility={{
        createdByUser: !isMobile,
        createdAt: !isMobile,
      }}
    />
  );
}

const FileNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledFilename = styled(BodySmallBold)`
  word-break: break-word;
`;

const IconContainer = styled.div``;
